/**
 * A FAILED LOAD NEVER READS AS EMPTY (queue item 201)
 * ===================================================
 *
 * 2026-10-06: one dropped Supabase connection made GET /api/context answer 500, and the
 * admin screen showed ZERO customers instead of an error. Every list screen did its own
 * `data.contexts || data.customers || []` and its own `catch {}`, so each one decided
 * separately -- and silently -- that a failure looks like an empty list.
 *
 * The rule, in one place:
 *   - a list comes out of a response through `listFrom` / `customerListFrom`, which THROW
 *     on a shape they do not recognise -- an unknown shape is a failed load, not "none";
 *   - `loadList` / `settledList` turn a request into `{ items, error }`: `items` is null
 *     whenever `error` is set, so a screen cannot render a failure as an empty list
 *     without visibly ignoring `error`;
 *   - the screen renders <LoadError> (components/LoadError.jsx) with a Retry.
 *
 * Plain JS on purpose: tests/test_failed_load_not_empty.py runs it under node.
 */

// The list a response carries: the response itself when it is an array, else the first of
// `keys` that holds an array. Anything else THROWS.
export function listFrom(response, ...keys) {
  if (Array.isArray(response)) return response
  if (response && typeof response === 'object') {
    for (const k of keys) {
      if (Array.isArray(response[k])) return response[k]
    }
  }
  const where = keys.length ? keys.join(' / ') : 'the response'
  throw new Error(`Unexpected response: no list under ${where}`)
}

// GET /api/context -> its `contexts` (older payloads: `customers`).
export function customerListFrom(response) {
  return listFrom(response, 'contexts', 'customers')
}

// A short, human message for a failed load.
export function loadErrorMessage(err) {
  const msg = (err && (err.message || String(err))) || 'Unknown error'
  return msg.length > 300 ? `${msg.slice(0, 300)}…` : msg
}

// Run `fetcher` and pull its list out. Never rejects; `items` is null when `error` is set.
export async function loadList(fetcher, ...keys) {
  try {
    return { items: listFrom(await fetcher(), ...keys), error: null }
  } catch (err) {
    return { items: null, error: loadErrorMessage(err) }
  }
}

// The same, for one entry of Promise.allSettled([...]).
export function settledList(result, ...keys) {
  if (!result || result.status !== 'fulfilled') {
    return { items: null, error: loadErrorMessage(result && result.reason) }
  }
  try {
    return { items: listFrom(result.value, ...keys), error: null }
  } catch (err) {
    return { items: null, error: loadErrorMessage(err) }
  }
}
